import { useState } from 'react';
import { motion } from 'framer-motion';
import { Gift, Wallet, PenLine, Send, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { GiftCardVoucher } from '@/components/GiftCardVoucher';
import GiftCardRequestModal from '@/components/GiftCardRequestModal';
import {
  GIFT_CARDS,
  GIFT_FAQS,
  GIFT_RULES,
  GIFT_BONUS_AMOUNT,
  GIFT_BONUS_DEADLINE_LABEL,
  isAperitivoBonusActive,
} from '@/data/giftcards2027';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const STEPS = [
  {
    icon: Wallet,
    titolo: '1. Scegli il valore',
    testo: 'Seleziona la Gift Card da 50 €, 100 € o 200 €.',
  },
  {
    icon: PenLine,
    titolo: '2. Aggiungi una dedica',
    testo: 'Personalizza il regalo con un messaggio per chi lo riceve.',
  },
  {
    icon: Send,
    titolo: '3. Regala La Villa',
    testo: 'Ricevi il buono digitale da inviare o stampare.',
  },
];

export default function GiftCardSection() {
  const [selectedCard, setSelectedCard] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const bonusActive = isAperitivoBonusActive();

  const openModal = (card) => {
    setSelectedCard(card);
    setModalOpen(true);
  };

  return (
    <>
      {/* ─── GIFT CARD ─── */}
      <section
        id="gift-card"
        className="py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-white to-stone-100 scroll-mt-28"
        data-testid="giftcard-section"
      >
        <div className="max-w-6xl mx-auto">
          {/* Intro + anteprima buono */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div {...fadeUp}>
              <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">La Villa Gift Card</p>
              <h2 className="font-heading text-4xl md:text-5xl text-night tracking-tight mb-6">
                Regala un pezzo d'estate.
              </h2>
              <p className="font-body text-stone-600 leading-relaxed mb-4">
                Una giornata in piscina, un aperitivo insieme, il piacere di ritagliarsi un momento alla Villa.
                Scegli il valore del regalo e lascia a chi lo riceve la libertà di viverlo durante la stagione 2027.
              </p>
              <p className="font-heading text-lg text-night italic">
                Tu scegli il regalo. Chi lo riceve sceglie il momento.
              </p>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.12 }} className="max-w-md w-full mx-auto lg:mx-0">
              <GiftCardVoucher amount={100} sample />
            </motion.div>
          </div>

          {/* Tre tagli */}
          <div className="grid md:grid-cols-3 gap-6 md:gap-8 items-stretch mb-6">
            {GIFT_CARDS.map((card, i) => {
              const showBonus = card.amount === 200 && bonusActive;
              return (
                <motion.div
                  key={card.amount}
                  className={`relative rounded-2xl p-7 flex flex-col bg-white shadow-lg ${
                    card.featured
                      ? 'border-2 border-champagne shadow-xl md:-translate-y-2'
                      : 'border border-stone-200'
                  }`}
                  {...fadeUp}
                  transition={{ delay: i * 0.08 }}
                  data-testid={`giftcard-card-${card.amount}`}
                >
                  {card.badge && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-champagne text-night text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full whitespace-nowrap">
                      {card.badge}
                    </span>
                  )}
                  {showBonus && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-night text-champagne text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full whitespace-nowrap border border-champagne/50">
                      {card.badgeBonus}
                    </span>
                  )}
                  <p className="font-body text-xs uppercase tracking-[0.2em] text-stone-400 mb-2 mt-1">Gift Card</p>
                  <h3 className="font-heading text-2xl text-night mb-1">{card.nome}</h3>
                  <p className="font-heading text-4xl text-champagne my-4">{card.amount} €</p>
                  <p className="font-body text-sm text-stone-600 mb-2">{card.credito}</p>
                  {showBonus && (
                    <p className="font-body text-sm text-champagne mb-2" data-testid="giftcard-200-promo">
                      {card.promo}
                    </p>
                  )}
                  <p className="font-body text-sm text-stone-500 leading-relaxed mb-6">{card.descrizione}</p>
                  <Button
                    onClick={() => openModal(card)}
                    className={`mt-auto rounded-full py-6 text-sm uppercase tracking-widest whitespace-normal h-auto ${
                      card.featured
                        ? 'bg-champagne text-night hover:bg-night hover:text-ivory'
                        : 'bg-night text-ivory hover:bg-champagne hover:text-night'
                    }`}
                    data-testid={`giftcard-buy-btn-${card.amount}`}
                  >
                    <Gift size={16} className="mr-2" /> {card.cta}
                  </Button>
                </motion.div>
              );
            })}
          </div>

          {/* Precisazioni */}
          <motion.div className="max-w-3xl mx-auto text-center space-y-2 mb-16" {...fadeUp}>
            {bonusActive ? (
              <p className="font-body text-xs text-stone-500 leading-relaxed" data-testid="giftcard-bonus-note">
                Il bonus di {GIFT_BONUS_AMOUNT} € è utilizzabile esclusivamente per l'aperitivo e resta separato dai 200 € di credito.
                La promozione è valida per gli acquisti entro il {GIFT_BONUS_DEADLINE_LABEL} incluso; dopo tale data la card da
                200 € resta disponibile senza il bonus.
              </p>
            ) : (
              <p className="font-body text-xs text-stone-500 leading-relaxed" data-testid="giftcard-bonus-expired-note">
                La promozione bonus aperitivo si è conclusa il {GIFT_BONUS_DEADLINE_LABEL}. I bonus già acquistati restano
                validi secondo le condizioni comunicate al momento dell'ordine.
              </p>
            )}
            <p className="font-body text-xs text-stone-400 leading-relaxed">
              I nomi delle card sono un invito, non un pacchetto: ogni Gift Card è un credito a valore,
              senza un numero fisso di ingressi o consumazioni.
            </p>
          </motion.div>

          {/* Come funziona */}
          <motion.div className="mb-16" {...fadeUp} data-testid="giftcard-steps">
            <h3 className="font-heading text-2xl md:text-3xl text-night text-center mb-10">Come funziona</h3>
            <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {STEPS.map((step) => (
                <div key={step.titolo} className="text-center px-4">
                  <div className="w-14 h-14 rounded-full bg-night flex items-center justify-center mx-auto mb-4">
                    <step.icon size={22} className="text-champagne" />
                  </div>
                  <h4 className="font-heading text-lg text-night mb-2">{step.titolo}</h4>
                  <p className="font-body text-sm text-stone-500 leading-relaxed">{step.testo}</p>
                </div>
              ))}
            </div>
            <p className="font-body text-xs text-stone-400 text-center mt-8 max-w-xl mx-auto">
              Pagamento online sicuro tramite Nexi XPay. Subito dopo la conferma ricevi il buono digitale
              via email, con codice univoco, pronto da inviare o stampare.
            </p>
          </motion.div>

          {/* Regole */}
          <motion.div
            id="gift-card-regole"
            className="bg-white border border-stone-200 rounded-2xl p-7 md:p-9 max-w-4xl mx-auto scroll-mt-28"
            {...fadeUp}
            data-testid="giftcard-rules-box"
          >
            <div className="flex items-center gap-2 mb-5">
              <Info size={18} className="text-champagne" />
              <h3 className="font-heading text-xl text-night">Condizioni di utilizzo</h3>
            </div>
            <ul className="grid md:grid-cols-2 gap-x-8 gap-y-3">
              {GIFT_RULES.map((rule) => (
                <li key={rule} className="font-body text-sm text-stone-600 leading-relaxed flex gap-2">
                  <span className="text-champagne shrink-0">·</span> {rule}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* ─── FAQ GIFT CARD ─── */}
      <section
        id="faq"
        className="py-20 md:py-28 px-6 md:px-12 lg:px-24 bg-white scroll-mt-28"
        data-testid="giftcard-faq-section"
      >
        <div className="max-w-3xl mx-auto">
          <motion.div className="text-center mb-12" {...fadeUp}>
            <p className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4">Domande frequenti</p>
            <h2 className="font-heading text-3xl md:text-4xl text-night">Gift Card — FAQ</h2>
          </motion.div>
          <motion.div {...fadeUp}>
            <Accordion type="single" collapsible className="w-full">
              {GIFT_FAQS.map((faq, i) => (
                <AccordionItem key={faq.q} value={`faq-${i}`} data-testid={`giftcard-faq-${i}`}>
                  <AccordionTrigger className="font-heading text-left text-night hover:text-champagne hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-stone-600 leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      <GiftCardRequestModal card={selectedCard} open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}
